import { format } from 'date-fns';
import type { WorkSheet } from 'xlsx-js-style';

type XLSXModule = typeof import('xlsx-js-style').default;
import type { CalculatorTab } from '../types/mortgageTypes';
import type { Country } from '../types/mortgageTypes';
import { formatCurrency } from './format';

// ─── Style constants ──────────────────────────────────────────────────────────

const S_DARK = {
  fill: { patternType: 'solid', fgColor: { rgb: '1E3A5F' } },
  font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
};

const S_BLUE = {
  fill: { patternType: 'solid', fgColor: { rgb: '2356F6' } },
  font: { bold: true, color: { rgb: 'FFFFFF' } },
};

const S_ALT = {
  fill: { patternType: 'solid', fgColor: { rgb: 'F3F4F6' } },
};

// ─── Cell builder ─────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function cell(v: string | number | null | undefined, s?: Record<string, any>) {
  const val = v ?? '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const obj: Record<string, any> = { v: val, t: typeof val === 'number' ? 'n' : 's' };
  if (s) obj.s = s;
  return obj;
}

// ─── Sheet row definition ─────────────────────────────────────────────────────

type RowType = 'section' | 'colheader' | 'data' | 'alt' | 'blank';

interface SheetRow {
  type: RowType;
  cells: (string | number | null | undefined)[];
  /** Optional per-cell Excel number format strings (index matches cells index). */
  numFmts?: (string | undefined)[];
}

// Pad a cells array to `len` with empty strings so section headers
// span the full column band with the correct background color.
function pad(
  cells: (string | number | null | undefined)[],
  len: number,
): (string | number | null | undefined)[] {
  const out = [...cells];
  while (out.length < len) out.push('');
  return out;
}

// ─── Worksheet builder ────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildSheet(rows: SheetRow[], colWidths: number[], xlsx: XLSXModule): Record<string, any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ws: Record<string, any> = {};
  let maxCol = 0;

  rows.forEach((row, r) => {
    maxCol = Math.max(maxCol, row.cells.length - 1);
    row.cells.forEach((val, c) => {
      const addr = xlsx.utils.encode_cell({ r, c });
      let style: Record<string, unknown> | undefined;
      if (row.type === 'section') style = S_DARK;
      else if (row.type === 'colheader') style = S_BLUE;
      else if (row.type === 'alt') style = S_ALT;
      const numFmt = row.numFmts?.[c];
      ws[addr] = cell(val, numFmt ? { ...style, numFmt } : style);
    });
  });

  ws['!ref'] = xlsx.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: rows.length - 1, c: maxCol },
  });
  ws['!cols'] = colWidths.map((w) => ({ wch: w }));
  return ws;
}

// ─── Label lookups ────────────────────────────────────────────────────────────

const LOAN_PROGRAM_LABELS: Record<string, string> = {
  conventional: 'Conventional',
  fha: 'FHA',
  va: 'VA',
  usda: 'USDA',
  jumbo: 'Jumbo',
  cmhc: 'CMHC',
};

const MORTGAGE_TYPE_LABELS: Record<string, string> = {
  fixed: 'Fixed',
  variable: 'Variable',
  adjustable: 'Adjustable (ARM)',
};

const FREQ_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  'semi-monthly': 'Semi-Monthly',
  'bi-weekly': 'Bi-Weekly',
  'accelerated-bi-weekly': 'Accelerated Bi-Weekly',
  weekly: 'Weekly',
  'accelerated-weekly': 'Accelerated Weekly',
};

function fmtExpense(
  exp: { type: 'percent' | 'amount'; value: number },
  homePrice: number,
  country: Country,
): string {
  if (exp.type === 'percent') {
    const annual = homePrice * (exp.value / 100);
    return `${exp.value}% (${formatCurrency(annual, country)}/yr)`;
  }
  return `${formatCurrency(exp.value, country)}/yr`;
}

// ─── Per-tab sheet ────────────────────────────────────────────────────────────

const TAB_NUM_COLS = 7;
const TAB_COL_WIDTHS = [34, 24, 16, 16, 16, 18, 18];

// Currency format applied to the six numeric columns of the amortization table.
// Col 0 is a date string; cols 1-6 are dollar amounts.
const CURRENCY_FMT = '$#,##0.00';
const AMORT_NUM_FMTS: (string | undefined)[] = [
  undefined,
  CURRENCY_FMT,
  CURRENCY_FMT,
  CURRENCY_FMT,
  CURRENCY_FMT,
  CURRENCY_FMT,
  CURRENCY_FMT,
];

function buildTabSheet(tab: CalculatorTab, xlsx: XLSXModule) {
  const {
    mortgageTerms: mt,
    annualExpenses: ae,
    extraPayments: ep,
    amortizationSchedule: sched,
  } = tab.data;

  const country: Country = mt.country ?? 'USA';
  const fmt = (n: number) => formatCurrency(n, country);
  const rows: SheetRow[] = [];

  // ── INPUTS ────────────────────────────────────────────────────────────────
  rows.push({ type: 'section', cells: pad(['INPUTS'], TAB_NUM_COLS) });
  rows.push({ type: 'colheader', cells: pad(['Field', 'Value'], TAB_NUM_COLS) });

  const inputData: [string, string | number][] = [
    ['Country', mt.country],
    ['Loan Program', LOAN_PROGRAM_LABELS[mt.loanProgram] ?? mt.loanProgram],
    ['Mortgage Type', MORTGAGE_TYPE_LABELS[mt.mortgageType] ?? mt.mortgageType],
    ['Home Price', fmt(mt.homePrice)],
    ['Down Payment', `${fmt(mt.downPaymentAmount)} (${mt.downPaymentPercentage.toFixed(1)}%)`],
    ['Interest Rate', `${mt.rate}%`],
    ['Amortization Period', `${mt.amortizationPeriod} years`],
    ['Payment Frequency', FREQ_LABELS[mt.paymentFrequency] ?? mt.paymentFrequency],
    ['Start Date', mt.startDate ? format(new Date(mt.startDate), 'MMMM yyyy') : '-'],
    ['Property Taxes', fmtExpense(ae.propertyTaxes, mt.homePrice, country)],
    ['Home Insurance', fmtExpense(ae.homeInsurance, mt.homePrice, country)],
    ['HOA / Utilities', fmtExpense(ae.hoaUtilities, mt.homePrice, country)],
  ];

  if (mt.loanProgram === 'conventional' && mt.pmi.value > 0) {
    inputData.push([
      'PMI',
      mt.pmi.type === 'percent'
        ? `${mt.pmi.value}% annual rate`
        : `${fmt(mt.pmi.value)}/mo`,
    ]);
  }

  if (mt.mortgageType === 'adjustable' && mt.armDetails) {
    const arm = mt.armDetails;
    inputData.push(
      ['ARM Initial Fixed Period', `${arm.initialFixedPeriod} years`],
      ['ARM Adjustment Frequency', `Every ${arm.adjustmentFrequency} months`],
      ['ARM Index Rate', `${arm.indexRate}%`],
      ['ARM Margin', `${arm.margin}%`],
      ['ARM Initial Adjustment Cap', `${arm.initialAdjustmentCap}%`],
      ['ARM Periodic Cap', `${arm.periodicCap}%`],
      ['ARM Lifetime Cap', `${arm.lifetimeCap}%`],
    );
  }

  if (mt.mortgageType === 'variable' && mt.variableDetails) {
    const vd = mt.variableDetails;
    inputData.push(
      ['Prime Rate', `${vd.primeRate}%`],
      ['Discount from Prime', `${vd.discount >= 0 ? '+' : ''}${vd.discount}%`],
    );
    if (mt.country === 'Canada') {
      inputData.push([
        'Variable Rate Type',
        vd.variableType === 'vrm' ? 'VRM (Fixed Payment)' : 'ARM (Adjustable Payment)',
      ]);
    }
    if (vd.rateChanges && vd.rateChanges.length > 0) {
      vd.rateChanges.forEach((rc) => {
        inputData.push([
          `Rate Forecast (Yr ${rc.year})`,
          `${rc.delta >= 0 ? '+' : ''}${rc.delta}%`,
        ]);
      });
    }
  }

  if (mt.loanProgram === 'cmhc' && mt.cmhcDetails) {
    const cmhc = mt.cmhcDetails;
    inputData.push(
      ['CMHC Premium Rate', `${cmhc.premiumRate}%`],
      ['Province', cmhc.province],
      ['Include Provincial Tax', cmhc.includeProvincialTax ? 'Yes' : 'No'],
    );
  }

  if (mt.canadianTermDetails) {
    const ctd = mt.canadianTermDetails;
    inputData.push(['Term Length', `${ctd.termLength} years`]);
    if (ctd.showRenewalScenario) {
      inputData.push(['Show renewal scenario', 'Yes']);
    }
    if (ctd.renewalRates != null && ctd.renewalRates.some((r) => r != null && Number.isFinite(r))) {
      ctd.renewalRates.forEach((r, i) => {
        if (r != null && Number.isFinite(r)) {
          inputData.push([`Renewal ${i + 1} rate (year ${ctd.termLength * (i + 1)})`, `${r}%`]);
        }
      });
    } else if (ctd.renewalRate !== undefined) {
      inputData.push(['Renewal rate (legacy)', `${ctd.renewalRate}%`]);
    }
  }

  if (mt.loanProgram === 'fha' && mt.fhaDetails) {
    const fha = mt.fhaDetails;
    inputData.push(
      ['FHA Upfront MIP', `${fha.upfrontMIP}%`],
      ['FHA Annual MIP Rate', `${fha.annualMIPRate}%`],
      ['FHA MIP Duration', `${fha.mipDurationYears} years`],
    );
  }

  if (mt.loanProgram === 'va' && mt.vaDetails) {
    const va = mt.vaDetails;
    inputData.push(
      ['VA Funding Fee Rate', `${va.fundingFeeRate}%`],
      ['First Time VA Use', va.isFirstTimeUse ? 'Yes' : 'No'],
      ['Disabled Veteran', va.isDisabledVeteran ? 'Yes' : 'No'],
    );
  }

  if (mt.loanProgram === 'usda' && mt.usdaDetails) {
    const usda = mt.usdaDetails;
    inputData.push(
      ['USDA Upfront Guarantee Fee', `${usda.upfrontFee}%`],
      ['USDA Annual Fee', `${usda.annualFee}%`],
    );
  }

  if (ep.amount > 0) {
    inputData.push(
      ['Extra Payment Amount', fmt(ep.amount)],
      [
        'Extra Payment Frequency',
        ep.frequency.charAt(0).toUpperCase() + ep.frequency.slice(1),
      ],
      ['Extra Payment Start', ep.startDate ? format(new Date(ep.startDate), 'MMMM yyyy') : '-'],
      [
        'Extra Payment End',
        ep.endDate ? format(new Date(ep.endDate), 'MMMM yyyy') : 'Until payoff',
      ],
    );
  }

  inputData.forEach(([label, value], i) => {
    rows.push({
      type: i % 2 === 1 ? 'alt' : 'data',
      cells: pad([label, value], TAB_NUM_COLS),
    });
  });

  rows.push({ type: 'blank', cells: [''] });

  // ── KEY INSIGHTS ──────────────────────────────────────────────────────────
  if (sched.length > 0) {
    const totalInterest = sched.reduce((s, e) => s + e.interestPayment, 0);
    const totalLoan = mt.homePrice - mt.downPaymentAmount;
    const totalCost = totalLoan + totalInterest;
    const totalExtra = sched.reduce((s, e) => s + (e.oneTimePayment ?? 0), 0);
    const payAmt = sched[0].payment;

    const annualExp = Object.values(ae).reduce((s, exp) => {
      return s + (exp.type === 'amount' ? exp.value : mt.homePrice * (exp.value / 100));
    }, 0);
    const isSubMonthly = mt.paymentFrequency !== 'monthly';
    const totalPmt = payAmt + (isSubMonthly ? annualExp / 12 / 2 : annualExp / 12);
    const lastDate = new Date(sched[sched.length - 1].date);
    const freqWord = FREQ_LABELS[mt.paymentFrequency] ?? 'Monthly';
    const pmtLabel = isSubMonthly
      ? `Monthly Bucket Payment (${freqWord} P+I)`
      : 'Monthly Payment (P+I)';
    const totalPmtLabel = isSubMonthly
      ? `Monthly Bucket Total (${freqWord} P+I + Expenses)`
      : 'Total Monthly Payment (incl. Expenses)';

    rows.push({ type: 'section', cells: pad(['KEY INSIGHTS'], TAB_NUM_COLS) });
    rows.push({ type: 'colheader', cells: pad(['Metric', 'Value'], TAB_NUM_COLS) });

    const totalPMI = sched.reduce((s, e) => s + (e.pmiPayment ?? 0), 0);

    const insights: [string, string][] = [
      [pmtLabel, fmt(payAmt)],
      [totalPmtLabel, fmt(totalPmt)],
      ['Total Interest', fmt(totalInterest)],
      ['Total Cost (Principal + Interest)', fmt(totalCost)],
      ['Total Extra Payments', fmt(totalExtra)],
      ...(totalPMI > 0 ? [['Total PMI', fmt(totalPMI)] as [string, string]] : []),
      ['Last Payment Date', format(lastDate, 'MMMM yyyy')],
    ];

    insights.forEach(([label, value], i) => {
      rows.push({
        type: i % 2 === 1 ? 'alt' : 'data',
        cells: pad([label, value], TAB_NUM_COLS),
      });
    });

    rows.push({ type: 'blank', cells: [''] });

    // ── AMORTIZATION SCHEDULE ──────────────────────────────────────────────
    rows.push({ type: 'section', cells: pad(['AMORTIZATION SCHEDULE'], TAB_NUM_COLS) });
    rows.push({
      type: 'colheader',
      cells: ['Date', 'Payment (P+I)', 'Principal', 'Interest', 'Expenses', 'Extra Payments', 'Balance'],
    });

    sched.forEach((entry, i) => {
      rows.push({
        type: i % 2 === 1 ? 'alt' : 'data',
        numFmts: AMORT_NUM_FMTS,
        cells: [
          format(new Date(entry.date), 'MMM yyyy'),
          entry.payment,
          entry.principalPayment,
          entry.interestPayment,
          entry.expensePayment,
          entry.oneTimePayment ?? 0,
          entry.remainingBalance,
        ],
      });
    });
  }

  return buildSheet(rows, TAB_COL_WIDTHS, xlsx);
}

// ─── Comparison sheet ─────────────────────────────────────────────────────────

function buildComparisonSheet(tabs: CalculatorTab[], xlsx: XLSXModule) {
  const titles = tabs.map((t) => t.title);
  const numCols = 1 + tabs.length;
  const rows: SheetRow[] = [];

  rows.push({ type: 'section', cells: pad(['COMPARISON'], numCols) });
  rows.push({ type: 'colheader', cells: ['Metric', ...titles] });

  const dataRows: (string | number)[][] = [
    [
      'Mortgage Type',
      ...tabs.map((t) => MORTGAGE_TYPE_LABELS[t.data.mortgageTerms.mortgageType] ?? t.data.mortgageTerms.mortgageType),
    ],
    [
      'Home Price',
      ...tabs.map((t) =>
        formatCurrency(t.data.mortgageTerms.homePrice, t.data.mortgageTerms.country),
      ),
    ],
    [
      'Principal',
      ...tabs.map((t) =>
        formatCurrency(
          t.data.mortgageTerms.homePrice - t.data.mortgageTerms.downPaymentAmount,
          t.data.mortgageTerms.country,
        ),
      ),
    ],
    [
      'Down Payment',
      ...tabs.map((t) => {
        const dp = t.data.mortgageTerms.downPaymentAmount;
        const pct =
          t.data.mortgageTerms.homePrice > 0
            ? Math.round((dp / t.data.mortgageTerms.homePrice) * 100)
            : 0;
        return `${formatCurrency(dp, t.data.mortgageTerms.country)} (${pct}%)`;
      }),
    ],
    ['Rate', ...tabs.map((t) => `${t.data.mortgageTerms.rate}%`)],
    [
      'Amortization Period',
      ...tabs.map((t) => `${t.data.mortgageTerms.amortizationPeriod} years`),
    ],
    [
      'Payment Frequency',
      ...tabs.map((t) => FREQ_LABELS[t.data.mortgageTerms.paymentFrequency] ?? t.data.mortgageTerms.paymentFrequency),
    ],
    [
      'Avg. Monthly Payment (incl. Expenses)',
      ...tabs.map((t) => {
        const s = t.data.amortizationSchedule;
        if (!s.length) return '-';
        // Schedule is always monthly rows (bi-weekly aggregated), so first row is one month's total
        const monthly = s[0].totalPayment;
        return formatCurrency(monthly, t.data.mortgageTerms.country);
      }),
    ],
    [
      'Total Interest',
      ...tabs.map((t) => {
        const total = t.data.amortizationSchedule.reduce((sum, e) => sum + e.interestPayment, 0);
        return formatCurrency(total, t.data.mortgageTerms.country);
      }),
    ],
    [
      'Total Cost',
      ...tabs.map((t) => {
        const totalInt = t.data.amortizationSchedule.reduce(
          (sum, e) => sum + e.interestPayment,
          0,
        );
        const principal =
          t.data.mortgageTerms.homePrice - t.data.mortgageTerms.downPaymentAmount;
        return formatCurrency(principal + totalInt, t.data.mortgageTerms.country);
      }),
    ],
    [
      'Total Extra Payments',
      ...tabs.map((t) => {
        const total = t.data.amortizationSchedule.reduce(
          (sum, e) => sum + (e.oneTimePayment ?? 0),
          0,
        );
        return formatCurrency(total, t.data.mortgageTerms.country);
      }),
    ],
    ...(tabs.some((t) => t.data.amortizationSchedule.some((e) => (e.pmiPayment ?? 0) > 0))
      ? [
          [
            'Total PMI',
            ...tabs.map((t) => {
              const total = t.data.amortizationSchedule.reduce(
                (sum, e) => sum + (e.pmiPayment ?? 0),
                0,
              );
              return total > 0
                ? formatCurrency(total, t.data.mortgageTerms.country)
                : '-';
            }),
          ],
        ]
      : []),
    [
      'Last Payment Date',
      ...tabs.map((t) => {
        const s = t.data.amortizationSchedule;
        if (!s.length) return '-';
        const firstDate = new Date(s[0].date);
        const lastDate = new Date(s[s.length - 1].date);
        const totalMonths =
          (lastDate.getFullYear() - firstDate.getFullYear()) * 12 +
          (lastDate.getMonth() - firstDate.getMonth()) + 1;
        const years = (totalMonths / 12).toFixed(1);
        return `${format(lastDate, 'MMMM yyyy')} (${years} yr)`;
      }),
    ],
  ];

  dataRows.forEach((row, i) => {
    rows.push({ type: i % 2 === 1 ? 'alt' : 'data', cells: row });
  });

  return buildSheet(rows, [38, ...tabs.map(() => 24)], xlsx);
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Excel rejects sheet names containing : \\ / ? * [ ] and caps length at 31. */
const EXCEL_SHEET_NAME_INVALID = /[:\\/?*[\]]/g;

function sanitizeExcelSheetName(raw: string, usedNames: Set<string>): string {
  let base = raw
    .replace(EXCEL_SHEET_NAME_INVALID, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!base) base = 'Scenario';
  base = base.slice(0, 31);

  let name = base;
  let n = 2;
  while (usedNames.has(name)) {
    const suffix = ` (${n})`;
    const truncated = base.slice(0, Math.max(1, 31 - suffix.length));
    name = (truncated + suffix).slice(0, 31);
    n++;
  }
  usedNames.add(name);
  return name;
}

export async function downloadMortgageExcel(tabs: CalculatorTab[]): Promise<void> {
  if (!tabs.length) return;

  const xlsx = (await import('xlsx-js-style')).default;
  const wb = xlsx.utils.book_new();

  const usedSheetNames = new Set<string>();
  if (tabs.length >= 2) usedSheetNames.add('Comparison');

  tabs.forEach((tab) => {
    const sheetName = sanitizeExcelSheetName(tab.title, usedSheetNames);
    xlsx.utils.book_append_sheet(wb, buildTabSheet(tab, xlsx) as WorkSheet, sheetName);
  });

  if (tabs.length >= 2) {
    xlsx.utils.book_append_sheet(wb, buildComparisonSheet(tabs, xlsx) as WorkSheet, 'Comparison');
  }

  const datetime = format(new Date(), 'yyyy-MM-dd-HH-mm');
  xlsx.writeFile(wb, `mortgage-compare-calculator-${datetime}.xlsx`);
}
